import React, { useState } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import axios from 'axios';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Clock, 
  CheckCircle2, 
  Send,
  User,
  MessageSquare,
  Globe,
  Building
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue 
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [activeForm, setActiveForm] = useState('simple');
  const [loading, setLoading] = useState(false);

  // Simple Form State
  const [simpleForm, setSimpleForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  // Consultation Form State
  const [consultationForm, setConsultationForm] = useState({
    name: '',
    email: '',
    phone: '',
    country: '',
    coverage_amount: '',
    insurance_type: '',
    current_coverage: '',
    message: '',
    preferred_contact_time: ''
  });

  const handleSimpleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact/simple`, simpleForm);
      toast({
        title: "Message Sent",
        description: "Thank you! We'll get back to you shortly.",
      });
      setSimpleForm({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConsultationSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact/consultation`, consultationForm);
      toast({
        title: "Request Submitted",
        description: "A specialist will contact you within 24 hours.",
      });
      setConsultationForm({
        name: '', email: '', phone: '', country: '',
        coverage_amount: '', insurance_type: '', current_coverage: '',
        message: '', preferred_contact_time: ''
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const countries = [
    'United States', 'UAE', 'India', 'Singapore', 'Malaysia',
    'Indonesia', 'Thailand', 'Vietnam', 'Philippines', 'United Kingdom', 'Other'
  ];

  const coverageAmounts = [
    '$500,000 - $1 Million',
    '$1 Million - $5 Million',
    '$5 Million - $10 Million',
    '$10 Million - $25 Million',
    '$25 Million+'
  ];

  const insuranceTypes = [
    'Term Life',
    'Whole Life',
    'Universal Life',
    'Premium Financing',
    'Not Sure - Need Guidance'
  ];

  return (
    <div className="bg-[#020617] min-h-screen pt-20" data-testid="contact-page">
      {/* Hero Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <span className="text-[#C5A059] text-sm uppercase tracking-widest mb-4 block">
              Get In Touch
            </span>
            <h1 className="font-serif text-4xl md:text-5xl text-white leading-tight mb-6">
              Contact Us
            </h1>
            <p className="text-slate-300 text-xl leading-relaxed">
              Ready to explore your options? Our specialists are here to provide confidential, 
              unbiased guidance tailored to your family's needs.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info + Forms */}
      <section className="py-12 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Information */}
            <div className="lg:col-span-1">
              <h2 className="font-serif text-2xl text-white mb-8">Contact Information</h2>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 rounded-lg bg-[#C5A059]/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-[#C5A059]" />
                  </div>
                  <div>
                    <p className="text-white font-medium mb-1">Email</p>
                    <a href="mailto:contact@shopcompareinsure.com" className="text-slate-400 hover:text-[#C5A059] text-sm">
                      contact@shopcompareinsure.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 rounded-lg bg-[#C5A059]/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-[#C5A059]" />
                  </div>
                  <div>
                    <p className="text-white font-medium mb-1">Phone</p>
                    <a href="tel:+14697893603" className="text-slate-400 hover:text-[#C5A059] text-sm">
                      +1 (469) 789-3603
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 rounded-lg bg-[#C5A059]/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-[#C5A059]" />
                  </div>
                  <div>
                    <p className="text-white font-medium mb-1">Hours</p>
                    <p className="text-slate-400 text-sm">
                      Monday - Friday: 9am - 6pm EST<br />
                      Weekend consultations by appointment
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 rounded-lg bg-[#C5A059]/10 flex items-center justify-center flex-shrink-0">
                    <Globe className="w-5 h-5 text-[#C5A059]" />
                  </div>
                  <div>
                    <p className="text-white font-medium mb-1">Global Coverage</p>
                    <p className="text-slate-400 text-sm">
                      Serving clients in USA & UAE
                    </p>
                  </div>
                </div>
              </div>

              {/* Trust Signals */}
              <div className="mt-12 p-6 bg-slate-900/50 border border-slate-800 rounded-lg">
                <h3 className="text-white font-medium mb-4">Our Commitment</h3>
                <ul className="space-y-3">
                  {[
                    "Confidential consultations",
                    "No obligation quotes",
                    "Unbiased carrier recommendations",
                    "Response within 24 hours"
                  ].map((item, index) => (
                    <li key={index} className="flex items-center space-x-2 text-sm text-slate-400">
                      <CheckCircle2 className="w-4 h-4 text-[#C5A059] flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Forms */}
            <div className="lg:col-span-2">
              {/* Form Toggle */}
              <div className="flex space-x-4 mb-8">
                <button
                  onClick={() => setActiveForm('simple')}
                  className={`px-6 py-3 text-sm uppercase tracking-wider transition-all ${
                    activeForm === 'simple'
                      ? 'bg-[#C5A059] text-[#020617]'
                      : 'bg-slate-800 text-slate-400 hover:text-white'
                  }`}
                  data-testid="simple-form-tab"
                >
                  Quick Message
                </button>
                <button
                  onClick={() => setActiveForm('consultation')}
                  className={`px-6 py-3 text-sm uppercase tracking-wider transition-all ${
                    activeForm === 'consultation'
                      ? 'bg-[#C5A059] text-[#020617]'
                      : 'bg-slate-800 text-slate-400 hover:text-white'
                  }`}
                  data-testid="consultation-form-tab"
                >
                  Request Consultation
                </button>
              </div>

              {/* Simple Contact Form */}
              {activeForm === 'simple' && (
                <form onSubmit={handleSimpleSubmit} className="space-y-6" data-testid="simple-contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">Name *</label>
                      <Input
                        type="text"
                        required
                        value={simpleForm.name}
                        onChange={(e) => setSimpleForm({ ...simpleForm, name: e.target.value })}
                        className="bg-slate-950/50 border-slate-800 focus:border-[#C5A059] text-white h-12"
                        placeholder="Your full name"
                        data-testid="simple-form-name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">Email *</label>
                      <Input
                        type="email"
                        required
                        value={simpleForm.email}
                        onChange={(e) => setSimpleForm({ ...simpleForm, email: e.target.value })}
                        className="bg-slate-950/50 border-slate-800 focus:border-[#C5A059] text-white h-12"
                        placeholder="you@example.com"
                        data-testid="simple-form-email"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-slate-400 mb-2">Phone</label>
                    <Input
                      type="tel"
                      value={simpleForm.phone}
                      onChange={(e) => setSimpleForm({ ...simpleForm, phone: e.target.value })}
                      className="bg-slate-950/50 border-slate-800 focus:border-[#C5A059] text-white h-12"
                      placeholder="+1 (555) 000-0000"
                      data-testid="simple-form-phone"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-slate-400 mb-2">Message *</label>
                    <Textarea
                      required
                      value={simpleForm.message}
                      onChange={(e) => setSimpleForm({ ...simpleForm, message: e.target.value })}
                      className="bg-slate-950/50 border-slate-800 focus:border-[#C5A059] text-white min-h-[150px]"
                      placeholder="How can we help you?"
                      data-testid="simple-form-message"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="inline-flex items-center justify-center bg-[#C5A059] text-[#020617] hover:bg-[#D4AF37] font-semibold px-8 py-4 uppercase tracking-wider text-sm transition-all disabled:opacity-50"
                    data-testid="simple-form-submit"
                  >
                    {loading ? 'Sending...' : 'Send Message'}
                    <Send className="w-4 h-4 ml-2" />
                  </button>
                </form>
              )}

              {/* Detailed Consultation Form */}
              {activeForm === 'consultation' && (
                <form onSubmit={handleConsultationSubmit} className="space-y-6" data-testid="consultation-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">Full Name *</label>
                      <Input
                        type="text"
                        required
                        value={consultationForm.name}
                        onChange={(e) => setConsultationForm({ ...consultationForm, name: e.target.value })}
                        className="bg-slate-950/50 border-slate-800 focus:border-[#C5A059] text-white h-12"
                        placeholder="Your full name"
                        data-testid="consultation-form-name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">Email *</label>
                      <Input
                        type="email"
                        required
                        value={consultationForm.email}
                        onChange={(e) => setConsultationForm({ ...consultationForm, email: e.target.value })}
                        className="bg-slate-950/50 border-slate-800 focus:border-[#C5A059] text-white h-12"
                        placeholder="you@example.com"
                        data-testid="consultation-form-email"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">Phone *</label>
                      <Input
                        type="tel"
                        required
                        value={consultationForm.phone}
                        onChange={(e) => setConsultationForm({ ...consultationForm, phone: e.target.value })}
                        className="bg-slate-950/50 border-slate-800 focus:border-[#C5A059] text-white h-12"
                        placeholder="+1 (555) 000-0000"
                        data-testid="consultation-form-phone"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">Country *</label>
                      <Select
                        value={consultationForm.country}
                        onValueChange={(value) => setConsultationForm({ ...consultationForm, country: value })}
                      >
                        <SelectTrigger className="bg-slate-950/50 border-slate-800 text-white h-12" data-testid="consultation-form-country">
                          <SelectValue placeholder="Select your country" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-900 border-slate-700">
                          {countries.map((country) => (
                            <SelectItem key={country} value={country} className="text-slate-300 hover:text-white focus:bg-slate-800">
                              {country}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">Coverage Amount</label>
                      <Select
                        value={consultationForm.coverage_amount}
                        onValueChange={(value) => setConsultationForm({ ...consultationForm, coverage_amount: value })}
                      >
                        <SelectTrigger className="bg-slate-950/50 border-slate-800 text-white h-12" data-testid="consultation-form-coverage">
                          <SelectValue placeholder="Estimated coverage needed" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-900 border-slate-700">
                          {coverageAmounts.map((amount) => (
                            <SelectItem key={amount} value={amount} className="text-slate-300 hover:text-white focus:bg-slate-800">
                              {amount}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm text-slate-400 mb-2">Insurance Type</label>
                      <Select
                        value={consultationForm.insurance_type}
                        onValueChange={(value) => setConsultationForm({ ...consultationForm, insurance_type: value })}
                      >
                        <SelectTrigger className="bg-slate-950/50 border-slate-800 text-white h-12" data-testid="consultation-form-type">
                          <SelectValue placeholder="Type of insurance" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-900 border-slate-700">
                          {insuranceTypes.map((type) => (
                            <SelectItem key={type} value={type} className="text-slate-300 hover:text-white focus:bg-slate-800">
                              {type}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-slate-400 mb-2">Current Coverage</label>
                    <Input
                      type="text"
                      value={consultationForm.current_coverage}
                      onChange={(e) => setConsultationForm({ ...consultationForm, current_coverage: e.target.value })}
                      className="bg-slate-950/50 border-slate-800 focus:border-[#C5A059] text-white h-12"
                      placeholder="Do you have existing life insurance? (Optional)"
                      data-testid="consultation-form-current"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-slate-400 mb-2">Additional Information</label>
                    <Textarea
                      value={consultationForm.message}
                      onChange={(e) => setConsultationForm({ ...consultationForm, message: e.target.value })}
                      className="bg-slate-950/50 border-slate-800 focus:border-[#C5A059] text-white min-h-[120px]"
                      placeholder="Tell us about your goals or any specific questions (Optional)"
                      data-testid="consultation-form-message"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-slate-400 mb-2">Preferred Contact Time</label>
                    <Input
                      type="text"
                      value={consultationForm.preferred_contact_time}
                      onChange={(e) => setConsultationForm({ ...consultationForm, preferred_contact_time: e.target.value })}
                      className="bg-slate-950/50 border-slate-800 focus:border-[#C5A059] text-white h-12"
                      placeholder="e.g., Weekday mornings, After 5pm EST"
                      data-testid="consultation-form-time"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="inline-flex items-center justify-center bg-[#C5A059] text-[#020617] hover:bg-[#D4AF37] font-semibold px-8 py-4 uppercase tracking-wider text-sm transition-all disabled:opacity-50"
                    data-testid="consultation-form-submit"
                  >
                    {loading ? 'Submitting...' : 'Request Consultation'}
                    <Send className="w-4 h-4 ml-2" />
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
